// The RB cattle indexes for the homepage rail, the top strip, and the index pages.
//
// ── WHAT THEY ARE ───────────────────────────────────────────────────────────
// Seven headline benchmarks plus twelve weight benchmarks, each a head-weighted
// average of lot-level auction sales over a trailing seven days across eleven
// states. Written once a night by `rb-index-snapshot-nightly` (08:10 UTC) and
// read here from the published record — never recomputed. A board read is
// ~47 ms against ~16 s to recompute all nineteen.
//
// ⚠️ THE METHOD IS CME'S, AND SAYING SO IS THE POINT. The CME Feeder Cattle
// Index — the number CME cash-settles feeder cattle futures against — is also a
// 7-day weighted average of 700–899 lb feeder steers, recalculated daily. This
// arrived at nearly the same construction independently. The one deliberate
// divergence is that CME divides dollars by POUNDS and these divide by HEAD
// (§2.1), because a producer sells a load and asks what his steers brought.
// Measured gap on the comparable cohort: $0.68/cwt. Do not "fix" it to match —
// see RB_INDEX_METHODOLOGY.md §2.1 for the three reasons and the measurements.
//
// ── NO SUPABASE CLIENT ──────────────────────────────────────────────────────
// Imported by a PRERENDERED document. The client touches localStorage at import
// and throws under Node. Reads go through `rpc` from rbCattleTicker, exactly like rbFeederFlash.ts. ⚠️ `rb_index_board` must also be
// in the /api/market allowlist or this returns nothing through the proxy — the
// RPC working against Supabase directly proves nothing about the path a
// visitor takes.

import { rpc, tickerAuthToken } from "@/lib/rbCattleTicker";
import { tickerAuthReady, tickerAuthSettled } from "@/lib/tickerAuth";
import { proofSession } from "@/lib/proofSession";

export type Coverage = "STRONG" | "MODERATE" | "LIMITED";

export interface RbIndexTile {
  slug: string;
  name: string;
  /** True for the seven on the board; false for the twelve weight
   *  benchmarks. Lets a list group them instead of running eighteen
   *  names together with no order a reader can see. */
  isHeadline: boolean;
  /** Pre-formatted, e.g. "$342.32". Formatted here so the prerender and the
   *  browser cannot disagree and trip a hydration mismatch. */
  value: string;
  /** ⚠️ SIGNED, e.g. "+0.17" / "−1.99". The owner's rule is negative red and
   *  positive green, and "sign hiding" is on this repo's bug-pattern list — a
   *  caller that receives an unsigned number cannot honour either.
   *  NULL means there was nothing to compare against, which is NOT a flat
   *  market. See `dayDir`. */
  dayChange: string | null;
  /** ⚠️ NULL when there is no comparison OR the market was flat. An arrow on a
   *  flat market is a lie in the direction it points. */
  dayDir: "up" | "down" | null;
  dayPct: string | null;
  /** ⚠️ TRUE WHEN NOTHING TRADED, which is not the same as a flat market.
   *  Measured over 216 days: ALL 31 Sundays moved exactly $0.00, because no
   *  cattle sell on a Sunday and the trailing window neither gains nor loses
   *  anything. A bare "0.00" looks identical to a market that traded and held
   *  — so the tile has to be able to say which. Detected by the sample size
   *  being unchanged, not by the day of the week: a holiday is the same fact. */
  noSales: boolean;
  /** The day the day-move describes, e.g. "Oct 4" — NOT "today". The record's
   *  latest day is usually yesterday, and on a Monday morning it is Sunday. */
  moveDayLabel: string;
  weekChange: string | null;
  weekDir: "up" | "down" | null;
  weekPct: string | null;
  /** "14,297" */
  head: string;
  barns: number;
  states: number;
  /** ⚠️ HOW WELL THE SAMPLE REPRESENTS THE MARKET — NOT a statement about
   *  prices. Always render it under the word COVERAGE: a bare "STRONG" beside a
   *  falling price reads as "strong market", which is the opposite of the
   *  number next to it. */
  coverage: Coverage;
  /** "Oct 3" — the sale day the figure describes, not the day it was computed. */
  asOf: string;
  asOfIso: string;
  /** Days between the sale day and today, so a screen can say the number is
   *  old rather than imply it is from today. */
  daysOld: number;
  /** Just the prices, oldest first — enough to draw a line and nothing more. */
  spark: number[];
  /** Plain-language statement of what this index pulls from, built from the
   *  definition rather than written by hand, so index number twenty explains
   *  itself with no code. */
  method: string;
  /** The weights the index covers, e.g. "700–899 lb" or "under 500 lb"; null for an index with no
   *  weight bound (cows, bulls). Shown after the card's name (owner, 2026-11-03: "so people know the
   *  weights they are looking at"). Optional: a tile prerendered before this field existed has none. */
  weightRange?: string | null;
  /** For the card's plain-English note: which cattle ("steers"), the smallest lot counted, the window
   *  in days and the lots in it. Optional for the same reason as weightRange. */
  cattleLabel?: string;
  minHead?: number;
  windowDays?: number;
  lots?: string;
  /** ⚠️ THE SHORT FORM, FOR THE HERO. `method` is a full paragraph and ran to
   *  FIVE LINES on a phone, pushing the price most of the way down the screen.
   *  A hero subtitle has to survive a 390px screen in two lines. The long form
   *  still appears, once, under "How this number is figured". */
  summary: string;
  /** ⚠️ A VISITOR'S CARD (Premium lock, 2026-10-13, option C: "everything
   *  except the big price"). `value` is empty and `spark` is the line's
   *  SHAPE, 0 to 1, already ending on the live point — never a price. */
  locked?: boolean;
}

const n2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = new Intl.NumberFormat("en-US");

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** ⚠️ U+2212 MINUS, not a hyphen. A hyphen next to tabular figures reads as a
 *  dash and is a different width, so a column of changes stops lining up. */
function signed(v: number | null): string | null {
  if (v === null) return null;
  if (Math.abs(v) < 0.005) return "0.00";
  return (v > 0 ? "+" : "−") + n2.format(Math.abs(v));
}

/**
 * ⚠️ A NICKEL, NOT HALF A CENT (owner, 2026-10-05). The threshold was 0.005, so
 * a ONE CENT move drew a full red arrow — the Feeder Heifer Index showed
 * "−0.01 ▼" on a $321 benchmark whose typical day moves $1.11. A penny is
 * four tenths of one percent of a normal day, and it rendered identically to a
 * real decline.
 *
 * Five cents is still small enough that anything a cattleman would act on
 * keeps its arrow, and large enough that noise does not get one.
 *
 * ⚠️ THE NUMBER STILL SHOWS. Only the arrow is withheld. "An arrow on a flat
 * market is a lie in the direction it points" — the lie is the arrow, not the
 * figure, so a penny move prints "−0.01" in the neutral colour and claims
 * nothing. Suppressing the number instead would be hiding data to tidy a tile.
 */
const FLAT_CWT = 0.05;

function dirOf(v: number | null): "up" | "down" | null {
  if (v === null || Math.abs(v) < FLAT_CWT) return null;
  return v > 0 ? "up" : "down";
}

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * ⚠️ THE UPPER BOUND IS EXCLUSIVE, AND THE LABEL HAS TO SAY SO. The engine
 * filters `avg_weight < p_weight_max` (migration …0011), so a definition stored
 * as 700/900 admits 700 up to 899.99 and never a 900 lb lot — measured on the
 * real records: heaviest included 899.93, and 28 lots at exactly 900 excluded.
 *
 * Printing "700–900 lb" was wrong in two ways a reader would catch:
 *
 *   1. It contradicts the CME claim this index makes. The CME Feeder Cattle
 *      Index covers 700-899 lb feeder steers. Ours IS 700-899; the label said
 *      otherwise, which made a true claim look sloppy. The owner spotted it.
 *   2. It made adjacent weight classes look like they OVERLAP — "400–500" next
 *      to "500–600" reads as though a 500 lb steer is in both. It is in exactly
 *      one.
 *
 * So a closed range prints its last INCLUDED pound.
 */
function weightPhrase(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${min}–${max - 1} lb`;
  // "under 500 lb" is already correct for an exclusive bound.
  if (max !== null) return `under ${max} lb`;
  if (min !== null) return `${min} lb and over`;
  return "all weights";
}

/** The two moving numbers for one index, as of right now. */
export interface RbIndexLive {
  slug: string;
  /** TODAY ALONE, pre-formatted, e.g. "$343.94". Null until something trades —
   *  NEVER "0.00", which would read as a price. */
  value: string | null;
  /**
   * THE INDEX ITSELF: the trailing window INCLUDING today so far. This is the
   * headline figure — it replaces last night's frozen close on every surface.
   *
   * ⚠️ IT IS NOT THE SAME NUMBER AS `value`, AND THE GAP IS LARGE. Measured
   * 2026-10-05 across all 18: median $6.53 apart, worst $22.65 (5wt Heifers,
   * index $387.96 against today $365.31). Today alone is thin in the morning —
   * only 3.7% of a sale day's lots are in by 8am — while the index has six
   * settled days underneath it and merely drifts. Never label them both
   * "live" on one screen: one is the quotable number, the other is the one
   * you watch.
   */
  indexValue: string | null;
  indexHead: string;
  indexHeadCount: number;
  indexLots: number;
  /** ⚠️ THESE DESCRIBE THE LIVE WINDOW, NOT LAST NIGHT'S. The tile printed a
   *  live price beside a frozen head count for a few hours on 2026-10-05 —
   *  $343.64 next to "14,297 hd" when it was built on 11,219 — which inverts
   *  the rule the head count exists to serve: the depth is there so a thin
   *  figure cannot pass for a deep one. */
  indexBarns: number | null;
  indexStates: number | null;
  /** Live move against yesterday's published close, pre-formatted and signed. */
  dayChange: string | null;
  dayDir: "up" | "down" | null;
  /** Live move against the close seven days back. */
  weekChange: string | null;
  weekDir: "up" | "down" | null;
  /** Graded on the live window with the engine's own thresholds. */
  coverage: Coverage | null;
  /** ⚠️ SHOW THIS WHENEVER YOU SHOW THE PRICE. Measured over 14 days, only
   *  3.7% of a sale day's lots have arrived by 8am and 22% by 10am, so an early
   *  figure is thin by nature. The panel this replaces carried the same rule:
   *  "the figure is allowed to be thin because the depth is printed under it.
   *  Remove it and the row lies." */
  head: string;
  headCount: number;
  lots: number;
  /** When the newest lot counted TODAY landed (ISO), or null before the first.
   *  The honest heartbeat of a sale day: it moves every few minutes while the
   *  index itself barely does (owner, 2026-10-08, "look more alive"). */
  lastAt: string | null;
  /** The newest lot in the whole window — never null while the index has any. */
  windowLastAt: string | null;
  /** The index has a live reading (what `indexValue` being set used to mean).
   *  A separate flag because a visitor's reading has no price but is still
   *  live — the card takes its head, states and changes from it either way. */
  hasIndex: boolean;
  /** Something has traded today (what `value` being set used to mean). */
  hasToday: boolean;
}

export const EMPTY_RB_INDEXES: RbIndexTile[] = [];

/**
 * Both live figures for every index: today alone, and the trailing window
 * including today — each head-weighted under that index's own rules.
 *
 * ⚠️ A SEPARATE CALL FROM THE BOARD ON PURPOSE. The board reads a snapshot
 * written once a night and is cached for fifteen minutes; this reads today and
 * is cached for fifteen SECONDS. Folding them together would force one of
 * those to be wrong.
 *
 * ⚠️ TWENTY SECONDS, AND THE FIRST MEASUREMENT OF THIS WAS WRONG. The gap
 * between consecutive ROWS is ~3.8s, but those are rows inside one ingest
 * batch and that says nothing about how often new data appears. Measured
 * properly on 2026-10-05: during a sale day 56-60 minutes out of every 60
 * receive new rows and the median gap between minutes-with-new-data is exactly
 * 60 seconds — ingest is continuous once sales are running, in roughly
 * 7.5-minute batches before about 11:00Z. So new data exists every minute, and
 * 20s polling against a 20s server cache holds the worst-case lag at 40s,
 * always inside the minute.
 */
/**
 * Indexes kept off every screen for now. Each is still computed and published
 * nightly, so bringing one back is deleting its line here, with no gap in its
 * record. Filtered in the readers every list goes through: live, board, the
 * public board, quotes, and the Daily Live Price readers (rbDailyPrice.ts).
 *   · rb_steer_heifer_500 — owner, 2026-10-12: "Remove the 500+ index for now
 *     in the app and desktop view"; he plans to retire it.
 *   · rb_cow, rb_slaughter_bull — owner, 2026-10-25: "hide the cow and bull
 *     indexes from the public webpage and app… we need to do more work on
 *     these before releasing them." Hidden, not retired: the Clara Light
 *     topics, the alert sweep and the app's class list follow this set.
 */
export const HIDDEN_INDEX_SLUGS: ReadonlySet<string> = new Set(["rb_steer_heifer_500", "rb_cow", "rb_slaughter_bull"]);
const shown = (r: Record<string, unknown>) => !HIDDEN_INDEX_SLUGS.has(String(r.slug ?? ""));

export async function fetchRbIndexLive(): Promise<Record<string, RbIndexLive>> {
  await tickerAuthSettled();
  const rows = ((await rpc<Record<string, unknown>>("rb_index_live", {})) ?? []).filter(shown);
  const out: Record<string, RbIndexLive> = {};
  for (const r of rows) {
    const price = num(r.price_cwt);
    const head = num(r.head) ?? 0;
    const ixPrice = num(r.index_cwt);
    const ixHead = num(r.index_head) ?? 0;
    const d1 = num(r.change_1d);
    const d7 = num(r.change_7d);
    out[String(r.slug)] = {
      slug: String(r.slug),
      // Null, not zero: nothing has traded yet is not a price of nothing.
      value: price === null || head === 0 ? null : `$${n2.format(price)}`,
      head: n0.format(head),
      headCount: head,
      lots: num(r.lots) ?? 0,
      lastAt: r.last_at == null ? null : String(r.last_at),
      windowLastAt: r.window_last_at == null ? null : String(r.window_last_at),
      indexValue: ixPrice === null || ixHead === 0 ? null : `$${n2.format(ixPrice)}`,
      indexHead: n0.format(ixHead),
      indexHeadCount: ixHead,
      indexLots: num(r.index_lots) ?? 0,
      indexBarns: num(r.index_barns),
      indexStates: num(r.index_states),
      dayChange: signed(d1),
      dayDir: dirOf(d1),
      weekChange: signed(d7),
      weekDir: dirOf(d7),
      coverage: (r.coverage as Coverage | null) ?? null,
      hasIndex: ixPrice !== null && ixHead !== 0,
      hasToday: price !== null && head !== 0,
    };
  }
  return out;
}

export async function fetchRbIndexes(headlineOnly = true): Promise<RbIndexTile[]> {
  await tickerAuthSettled();
  const rows = (
    (await rpc<Record<string, unknown>>("rb_index_board", {
      p_headline_only: headlineOnly ? "true" : "false",
      p_spark_days: 180,
    })) ?? []
  ).filter(shown);
  return rows.map((r) => boardTile(r, false)).filter((t): t is RbIndexTile => t !== null);
}

/**
 * What a visitor without Premium sees: the homepage cards with every price
 * taken out (Premium lock, 2026-10-13). One read, `rb_index_public_board`,
 * which is the same for everyone and so sits on the CDN like the old board.
 */
export async function fetchRbIndexPublic(): Promise<{ tiles: RbIndexTile[]; live: Record<string, RbIndexLive> }> {
  const rows = ((await rpc<Record<string, unknown>>("rb_index_public_board", {})) ?? []).filter(shown);
  const tiles = rows.map((r) => boardTile(r, true)).filter((t): t is RbIndexTile => t !== null);
  const live: Record<string, RbIndexLive> = {};
  for (const r of rows) {
    const l = r.live as Record<string, unknown> | null | undefined;
    if (!l || typeof l !== "object") continue;
    const head = num(l.head) ?? 0;
    const ixHead = num(l.index_head) ?? 0;
    const d1 = num(l.change_1d);
    const d7 = num(l.change_7d);
    live[String(r.slug)] = {
      slug: String(r.slug),
      value: null,
      indexValue: null,
      head: n0.format(head),
      headCount: head,
      lots: num(l.lots) ?? 0,
      lastAt: l.last_at == null ? null : String(l.last_at),
      windowLastAt: l.window_last_at == null ? null : String(l.window_last_at),
      indexHead: n0.format(ixHead),
      indexHeadCount: ixHead,
      indexLots: num(l.index_lots) ?? 0,
      indexBarns: num(l.index_barns),
      indexStates: num(l.index_states),
      dayChange: signed(d1),
      dayDir: dirOf(d1),
      weekChange: signed(d7),
      weekDir: dirOf(d7),
      coverage: (l.coverage as Coverage | null) ?? null,
      hasIndex: l.has_index === true,
      hasToday: l.has_today === true,
    };
  }
  return { tiles, live };
}

/** One board row as a card. `locked`: the public board's row, which carries
 *  no price and a line already reduced to its shape. */
function boardTile(r: Record<string, unknown>, locked: boolean): RbIndexTile | null {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
  const price = num(r.price_cwt);
  // No published figure. The tile is dropped rather than shown empty — a
  // dash in a row of prices reads as a broken page, not as "no sale".
  if (!locked && price === null) return null;

  const day = num(r.change_1d);
  const week = num(r.change_7d);
  const asOfIso = String(r.as_of ?? today);
  const cattle =
    r.cohort === "cow" ? "Cows"
    : r.cohort === "slaughter_bull" ? "Bulls"
    : (r.cattle_type as string | null) ?? "Steers and heifers";

  const coverage = (String(r.coverage ?? "LIMITED") as Coverage);

  return {
    slug: String(r.slug ?? ""),
    name: String(r.name ?? ""),
    isHeadline: r.is_headline === true,
    value: price === null ? "" : `$${n2.format(price)}`,
    dayChange: signed(day),
    dayDir: dirOf(day),
    dayPct: signed(num(r.change_1d_pct)),
    noSales:
      num(r.head) !== null &&
      num(r.head) === num(r.prior_head) &&
      (day === null || Math.abs(day) < 0.005),
    moveDayLabel: dayLabel(asOfIso),
    weekChange: signed(week),
    weekDir: dirOf(week),
    weekPct: signed(num(r.change_7d_pct)),
    head: n0.format(num(r.head) ?? 0),
    barns: num(r.barns) ?? 0,
    states: num(r.states) ?? 0,
    coverage,
    asOf: dayLabel(asOfIso),
    asOfIso,
    daysOld: num(r.days_old) ?? 0,
    spark: Array.isArray(r.spark)
      ? (r.spark as unknown[])
          .map((p) => (locked ? num(p) : num((p as Record<string, unknown> | null)?.p)))
          .filter((n): n is number => n !== null)
      : [],
    cattleLabel: cattle.toLowerCase(),
    minHead: num(r.min_head) ?? 10,
    windowDays: num(r.window_days) ?? 7,
    lots: n0.format(num(r.transactions) ?? 0),
    weightRange:
      num(r.weight_min) === null && num(r.weight_max) === null ? null : weightPhrase(num(r.weight_min), num(r.weight_max)),
    summary:
      `${cattle}, ${weightPhrase(num(r.weight_min), num(r.weight_max))} · ` +
      `${num(r.states) ?? 0} states · ${num(r.window_days) ?? 7}-day head-weighted average`,
    method:
      `${cattle}, ${weightPhrase(num(r.weight_min), num(r.weight_max))}. ` +
      `Lots of ${num(r.min_head) ?? 10}+ head. ${num(r.states) ?? 0} states. ` +
      `Last ${num(r.window_days) ?? 7} days through ${dayLabel(asOfIso)}: ` +
      `${n0.format(num(r.head) ?? 0)} head in ${n0.format(num(r.transactions) ?? 0)} lots ` +
      `at ${num(r.barns) ?? 0} barns. Each lot counts by its head. ` +
      `The line shows the last 30 days.`,
    ...(locked ? { locked: true } : {}),
  };
}

/* ── The quote board and the close table ─────────────────────────────────────
 * Owner, 2026-10-06: "a table by day that looks like this from barchart for
 * each index... we would need to match their columns as much as possible".
 * Numbers stay numbers here; the tables format them, because a CSV wants the
 * raw figure and the screen wants "343.80s".
 */

/** Must match the p_slug enum in api/market.ts — rbIndexQuotes.test.ts pins it. */
/**
 * Go-live (owner, 2026-10-08): "We have lots of revisions up till then so I
 * don't want it to look bad for us." Before this date the public pages are in
 * the build period: the corrections log is empty, version tables show one
 * line, and every standing value reads as version 1. The database holds the
 * same date on rb_index_close_policy.go_live and numbers revisions from it;
 * the record itself is complete throughout. Central time, like the sale day.
 */
export const RB_INDEX_GO_LIVE = "2026-11-01";
export function rbIndexIsLive(now: Date = new Date()): boolean {
  return now.toLocaleDateString("en-CA", { timeZone: "America/Chicago" }) >= RB_INDEX_GO_LIVE;
}

export const RB_INDEX_SLUGS = [
  "rb_feeder_steer", "rb_feeder_heifer", "rb_calf", "rb_steer_heifer_500", "rb_cow", "rb_slaughter_bull",
  "rb_4wt_steers", "rb_4wt_heifers", "rb_5wt_steers", "rb_5wt_heifers", "rb_6wt_steers", "rb_6wt_heifers",
  "rb_7wt_steers", "rb_7wt_heifers", "rb_8wt_steers", "rb_8wt_heifers", "rb_9wt_steers", "rb_9wt_heifers",
  // The four added 2026-10-08 for the weekly grid: 300s and 1000s by sex.
  "rb_3wt_steers", "rb_3wt_heifers", "rb_10wt_steers", "rb_10wt_heifers",
] as const;

export type HistoryDays = 30 | 90 | 400;

export interface RbIndexQuote {
  slug: string;
  code: string;
  name: string;
  isHeadline: boolean;
  /** The sale day the figure describes. */
  asOf: string;
  /** True when `last` is a written close (shown with an "s"); false while the day is still trading. */
  settled: boolean;
  last: number | null;
  change: number | null;
  changePct: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  prev: number | null;
  prevAsOf: string | null;
  head: number;
  lots: number;
  barns: number;
  states: number;
  coverage: Coverage | null;
  status: string;
}

export interface RbIndexHistoryRow {
  asOf: string;
  close: number;
  change: number | null;
  changePct: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  prev: number | null;
  head: number;
  lots: number;
  barns: number;
  states: number;
  coverage: Coverage | null;
  revision: number;
  status: string;
  /** Minutes between the readings behind open / high / low: 5 live, 15 replayed; null when none. */
  stepMinutes: number | null;
  /** Lots that day with no arrival time (counted from the first reading) and lots that arrived after the day ended (in the close, in no reading). */
  unknownArrivalLots: number;
  lateLots: number;
  /** Head on the same weekday a week earlier (as_of − 7), and the difference. Null when that day has no close. */
  headWkAgo: number | null;
  headChgWk: number | null;
  headChgWkPct: number | null;
  /** The r2's one line: what the close was before this version, when it changed, why. Null on version 1. */
  beforeCwt: number | null;
  revisedAt: string | null;
  revisionReason: string | null;
}

export async function fetchRbIndexQuotes(): Promise<RbIndexQuote[]> {
  await tickerAuthSettled();
  const rows = ((await rpc<Record<string, unknown>>("rb_index_quotes", {})) ?? []).filter(shown);
  return rows.map((r) => ({
    slug: String(r.slug ?? ""),
    code: String(r.code ?? ""),
    name: String(r.name ?? ""),
    isHeadline: r.is_headline === true,
    asOf: String(r.as_of ?? ""),
    settled: r.settled !== false,
    last: num(r.last_cwt),
    change: num(r.change),
    changePct: num(r.change_pct),
    open: num(r.open_cwt),
    high: num(r.high_cwt),
    low: num(r.low_cwt),
    prev: num(r.prev_cwt),
    prevAsOf: r.prev_as_of == null ? null : String(r.prev_as_of),
    head: num(r.head) ?? 0,
    lots: num(r.lots) ?? 0,
    barns: num(r.barns) ?? 0,
    states: num(r.states) ?? 0,
    coverage: (r.coverage as Coverage | null) ?? null,
    status: String(r.status ?? ""),
  }));
}

export async function fetchRbIndexHistory(slug: string, days: HistoryDays): Promise<RbIndexHistoryRow[]> {
  await tickerAuthSettled();
  const rows = (await rpc<Record<string, unknown>>("rb_index_history", { p_slug: slug, p_days: String(days) })) ?? [];
  return rows
    .map((r): RbIndexHistoryRow | null => {
      const close = num(r.close_cwt);
      if (close === null) return null;
      return {
        asOf: String(r.as_of ?? ""),
        close,
        change: num(r.change),
        changePct: num(r.change_pct),
        open: num(r.open_cwt),
        high: num(r.high_cwt),
        low: num(r.low_cwt),
        prev: num(r.prev_cwt),
        head: num(r.head) ?? 0,
        lots: num(r.lots) ?? 0,
        barns: num(r.barns) ?? 0,
        states: num(r.states) ?? 0,
        coverage: (r.coverage as Coverage | null) ?? null,
        revision: num(r.revision) ?? 1,
        status: String(r.status ?? ""),
        stepMinutes: num(r.step_minutes),
        unknownArrivalLots: num(r.unknown_arrival_lots) ?? 0,
        lateLots: num(r.late_lots) ?? 0,
        beforeCwt: num(r.before_cwt),
        revisedAt: r.revised_at == null ? null : String(r.revised_at),
        revisionReason: r.revision_reason == null ? null : String(r.revision_reason),
        headWkAgo: num(r.head_wk_ago),
        headChgWk: num(r.head_chg_wk),
        headChgWkPct: num(r.head_chg_wk_pct),
      };
    })
    .filter((r): r is RbIndexHistoryRow => r !== null);
}

/* ── The unlisted proof pages ─────────────────────────────────────────────────
 * RB_INDEX_PROOF_PLAN.md §4. A keyed RPC is never in the /api/market allowlist
 * (a keyed response must not sit on a shared CDN cache) and the key must not
 * appear in a proxy URL, so these post straight to PostgREST with the
 * publishable key. The RPC checks the share key inside its own body — or
 * the caller's own session: a signed-in admin needs no key, so the bearer is
 * the user's token when there is one (same rule as rpc() in rbCattleTicker).
 */
async function rpcKeyed<T>(fn: string, params: Record<string, unknown>): Promise<T | null> {
  const url = import.meta.env?.VITE_SUPABASE_URL;
  const key = import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) return null;
  // ⚠️ THE SESSION, TWO WAYS. First the hand-off the ticker library gets at
  // startup (awaited, raced against two seconds). If that holds nothing, read
  // the sign-in client's own storage directly — the admin desk opens on that
  // same session, so if it exists here, this finds it. The owner, signed in
  // as admin on the real site, was still told he needed a key (2026-10-07).
  await Promise.race([tickerAuthReady, new Promise<void>((r) => setTimeout(r, 2000))]);
  let bearer = tickerAuthToken();
  if (!bearer) bearer = (await proofSession())?.token ?? null;
  const res = await fetch(`${url}/rest/v1/rpc/${fn}`, {
    method: "POST",
    headers: { apikey: key, authorization: `Bearer ${bearer ?? key}`, "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // ⚠️ ONLY THE SERVER'S OWN "not valid" IS A KEY REFUSAL. Every other 400
    // is a different error and is shown verbatim — folding them all into
    // "not valid" hid the real failure for an hour on 2026-10-07. The bearer
    // that was sent rides on the error so a refusal can be diagnosed with
    // the same headers.
    let msg: string;
    try { msg = String((JSON.parse(body) as { message?: string }).message ?? body); } catch { msg = body; }
    const err = new Error(/not valid/.test(msg)
      ? `This link is not valid.${bearer ? " [session sent]" : " [no session]"}`
      : `HTTP ${res.status} from ${fn}: ${msg.slice(0, 240)}`) as Error & { bearer?: string | null };
    err.bearer = bearer;
    throw err;
  }
  return (await res.json()) as T;
}

export interface ReconRow {
  slug: string; code: string; name: string; asOf: string; revision: number;
  publishedCwt: number; recomputedCwt: number | null; deltaCwt: number | null;
  publishedHead: number; recomputedHead: number | null;
  publishedLots: number; recomputedLots: number | null;
  publishedPounds: number | null; recomputedPounds: number | null;
  match: boolean; priceMatch: boolean; headMatch: boolean; lotsMatch: boolean; poundsMatch: boolean;
  lowCwt: number | null; highCwt: number | null; lastCwt: number | null;
  closeInRange: boolean | null; lastEqClose: boolean | null;
  unknownArrivalLots: number | null; lateLots: number | null;
}
export interface ReconRun {
  id: number; startedAt: string; finishedAt: string; days: number;
  checked: number; matched: number; mismatched: number; marksChecked: number; marksOk: number; ms: number | null;
}
export interface ReconReport {
  run: ReconRun | null;
  rows: ReconRow[];
  runs: ReconRun[];
  revisions: { daysWithRevisions: number; rowsAboveRev1: number; withoutReason: number };
  generatedAt: string;
}

function reconRun(r: Record<string, unknown> | null): ReconRun | null {
  if (!r) return null;
  return {
    id: num(r.id) ?? 0, startedAt: String(r.started_at ?? ""), finishedAt: String(r.finished_at ?? ""), days: num(r.days) ?? 0,
    checked: num(r.checked) ?? 0, matched: num(r.matched) ?? 0, mismatched: num(r.mismatched) ?? 0,
    marksChecked: num(r.marks_checked) ?? 0, marksOk: num(r.marks_ok) ?? 0, ms: num(r.ms),
  };
}

export async function fetchRbIndexReconciliation(key: string | null): Promise<ReconReport | null> {
  const d = await rpcKeyed<Record<string, unknown>>("rb_index_reconciliation", { p_key: key || null });
  if (!d) return null;
  const rows = ((d.rows as Record<string, unknown>[] | undefined) ?? []).map((r): ReconRow => ({
    slug: String(r.slug ?? ""), code: String(r.code ?? ""), name: String(r.name ?? ""), asOf: String(r.as_of ?? ""),
    revision: num(r.revision) ?? 1,
    publishedCwt: num(r.published_cwt) ?? 0, recomputedCwt: num(r.recomputed_cwt), deltaCwt: num(r.delta_cwt),
    publishedHead: num(r.published_head) ?? 0, recomputedHead: num(r.recomputed_head),
    publishedLots: num(r.published_lots) ?? 0, recomputedLots: num(r.recomputed_lots),
    publishedPounds: num(r.published_pounds), recomputedPounds: num(r.recomputed_pounds),
    match: r.match === true, priceMatch: r.price_match === true, headMatch: r.head_match === true,
    lotsMatch: r.lots_match === true, poundsMatch: r.pounds_match === true,
    lowCwt: num(r.low_cwt), highCwt: num(r.high_cwt), lastCwt: num(r.last_cwt),
    closeInRange: r.close_in_range == null ? null : r.close_in_range === true,
    lastEqClose: r.last_eq_close == null ? null : r.last_eq_close === true,
    unknownArrivalLots: num(r.unknown_arrival_lots), lateLots: num(r.late_lots),
  }));
  const rev = (d.revisions as Record<string, unknown> | undefined) ?? {};
  return {
    run: reconRun((d.run as Record<string, unknown> | null) ?? null),
    rows,
    runs: ((d.runs as Record<string, unknown>[] | undefined) ?? []).map((r) => reconRun(r)!),
    revisions: { daysWithRevisions: num(rev.days_with_revisions) ?? 0, rowsAboveRev1: num(rev.rows_above_rev1) ?? 0, withoutReason: num(rev.without_reason) ?? 0 },
    generatedAt: String(d.generated_at ?? ""),
  };
}

/* ── The calculation sheet (RB_INDEX_PROOF_PLAN.md §2A) ──────────────────── */
export interface SheetLot {
  id: number; date: string; arrivedAt: string | null; barn: string; state: string; cattleType: string;
  avgWeight: number; head: number; priceCwt: number; dollars: number; movedIn: boolean;
  rawType: string | null; rawWeight: number | null;
}
export interface SheetDecision {
  lotId: number; date: string; barn: string; state: string; cattleType: string; avgWeight: number; head: number;
  priceCwt: number; dollars: number; status: string; reason: string | null; note: string | null; movedTo: string | null;
  decidedAt: string; role: string; effect: string;
}
export interface SheetRevision {
  revision: number; status: string; reason: string | null; computedAt: string; priceCwt: number; head: number; lots: number;
  pounds: number | null; coverage: string | null; methodologyVersion: string | null;
  deltaCwt: number | null; deltaHead: number | null; deltaLots: number | null; decisionsBetween: number;
}
export interface Sheet {
  mode: "close" | "live";
  definition: { slug: string; code: string; name: string; cohort: string; cattleType: string | null; weightMin: number | null;
                weightMax: number | null; windowDays: number; methodologyVersion: string | null; isHeadline: boolean };
  rules: { states: string[]; priceFloorCwt: number; minHead: { culls: number; under500: number; over500: number };
           feederBandLb: string; publicationFloorHead: number; formula: string; windowClock: string };
  window: { from: string; to: string; startAt: string; endAt: string };
  published: { priceCwt: number; head: number; lots: number; pounds: number | null; barns: number; states: number; coverage: string | null;
               publication: string; status: string; revision: number; revisionReason: string | null; computedAt: string;
               methodologyVersion: string | null; revisionsTotal: number } | null;
  live: { indexCwt: number | null; indexHead: number; indexLots: number; indexBarns: number; indexStates: number; coverage: string | null;
          windowEnd: string } | null;
  recompute: { dollars: number; head: number; lots: number; pounds: number; priceCwt: number | null; barns: number; states: number;
               lowPrice: number | null; highPrice: number | null; avgWeight: number | null; topBarnPct: number | null; top3BarnPct: number | null;
               publication: string; coverage: string; matchesPublished: boolean | null; deltaCwt: number | null };
  coverageTests: { label: string; value: number | null; strong: number; moderate: number; direction: "min" | "max" }[];
  funnel: { stepOrder: number; step: string; lots: number; head: number }[];
  states: { state: string; lots: number; head: number; dollars: number; vwap: number | null; barns: number; sharePct: number | null }[];
  barns: { state: string; barn: string; lots: number; head: number; dollars: number; vwap: number | null; sharePct: number | null }[];
  lotsTotal: number; lotsInline: boolean; lots: SheetLot[];
  decisions: SheetDecision[];
  revisions: SheetRevision[];
  pendingRevision: boolean | null;
  readings: { open: number; high: number; low: number; last: number; firstAt: string; lastAt: string; marks: number; stepMinutes: number;
              unknownArrivalLots: number; lateLots: number } | null;
  vintage: { source: string; firstArrival: string | null; lastArrival: string | null; unknownArrivalLots: number; generatedAt: string };
}

const lot = (r: Record<string, unknown>): SheetLot => ({
  id: num(r.id) ?? 0, date: String(r.date ?? ""), arrivedAt: r.arrived_at == null ? null : String(r.arrived_at),
  barn: String(r.barn ?? ""), state: String(r.state ?? ""), cattleType: String(r.cattle_type ?? ""),
  avgWeight: num(r.avg_weight) ?? 0, head: num(r.head) ?? 0, priceCwt: num(r.price_cwt) ?? 0, dollars: num(r.dollars) ?? 0,
  movedIn: r.moved_in === true, rawType: r.raw_type == null ? null : String(r.raw_type), rawWeight: num(r.raw_weight),
});

export async function fetchRbIndexSheet(slug: string, asOf: string | null, key: string | null): Promise<Sheet | null> {
  const d = await rpcKeyed<Record<string, unknown>>("rb_index_sheet", { p_slug: slug, p_as_of: asOf, p_key: key || null });
  if (!d) return null;
  const def = (d.definition as Record<string, unknown>) ?? {};
  const rules = (d.rules as Record<string, unknown>) ?? {};
  const mh = (rules.min_head as Record<string, unknown>) ?? {};
  const win = (d.window as Record<string, unknown>) ?? {};
  const pub = d.published as Record<string, unknown> | null;
  const lv = d.live as Record<string, unknown> | null;
  const rc = (d.recompute as Record<string, unknown>) ?? {};
  const rd = d.readings as Record<string, unknown> | null;
  const vt = (d.vintage as Record<string, unknown>) ?? {};
  const arr = (k: string) => (d[k] as Record<string, unknown>[] | undefined) ?? [];
  return {
    mode: d.mode === "live" ? "live" : "close",
    definition: { slug: String(def.slug ?? ""), code: String(def.code ?? ""), name: String(def.name ?? ""), cohort: String(def.cohort ?? ""),
      cattleType: def.cattle_type == null ? null : String(def.cattle_type), weightMin: num(def.weight_min), weightMax: num(def.weight_max),
      windowDays: num(def.window_days) ?? 7, methodologyVersion: def.methodology_version == null ? null : String(def.methodology_version),
      isHeadline: def.is_headline === true },
    rules: { states: (rules.states as string[]) ?? [], priceFloorCwt: num(rules.price_floor_cwt) ?? 50,
      minHead: { culls: num(mh.culls) ?? 1, under500: num(mh.under_500_lb) ?? 5, over500: num(mh["500_lb_and_over"]) ?? 10 },
      feederBandLb: String(rules.feeder_band_lb ?? ""), publicationFloorHead: num(rules.publication_floor_head) ?? 10,
      formula: String(rules.formula ?? ""), windowClock: String(rules.window_clock ?? "") },
    window: { from: String(win.from ?? ""), to: String(win.to ?? ""), startAt: String(win.start_at ?? ""), endAt: String(win.end_at ?? "") },
    published: pub ? { priceCwt: num(pub.price_cwt) ?? 0, head: num(pub.head) ?? 0, lots: num(pub.lots) ?? 0, pounds: num(pub.pounds),
      barns: num(pub.barns) ?? 0, states: num(pub.states) ?? 0, coverage: pub.coverage == null ? null : String(pub.coverage),
      publication: String(pub.publication ?? ""), status: String(pub.status ?? ""), revision: num(pub.revision) ?? 1,
      revisionReason: pub.revision_reason == null ? null : String(pub.revision_reason), computedAt: String(pub.computed_at ?? ""),
      methodologyVersion: pub.methodology_version == null ? null : String(pub.methodology_version), revisionsTotal: num(pub.revisions_total) ?? 1 } : null,
    live: lv ? { indexCwt: num(lv.index_cwt), indexHead: num(lv.index_head) ?? 0, indexLots: num(lv.index_lots) ?? 0,
      indexBarns: num(lv.index_barns) ?? 0, indexStates: num(lv.index_states) ?? 0, coverage: lv.coverage == null ? null : String(lv.coverage),
      windowEnd: String(lv.window_end ?? "") } : null,
    recompute: { dollars: num(rc.dollars) ?? 0, head: num(rc.head) ?? 0, lots: num(rc.lots) ?? 0, pounds: num(rc.pounds) ?? 0,
      priceCwt: num(rc.price_cwt), barns: num(rc.barns) ?? 0, states: num(rc.states) ?? 0, lowPrice: num(rc.low_price), highPrice: num(rc.high_price),
      avgWeight: num(rc.avg_weight), topBarnPct: num(rc.top_barn_pct), top3BarnPct: num(rc.top3_barn_pct),
      publication: String(rc.publication ?? ""), coverage: String(rc.coverage ?? ""),
      matchesPublished: rc.matches_published == null ? null : rc.matches_published === true, deltaCwt: num(rc.delta_cwt) },
    coverageTests: arr("coverage_tests").map((t) => ({ label: String(t.label ?? ""), value: num(t.value), strong: num(t.strong) ?? 0,
      moderate: num(t.moderate) ?? 0, direction: t.direction === "max" ? "max" : "min" })),
    funnel: arr("funnel").map((f) => ({ stepOrder: num(f.step_order) ?? 0, step: String(f.step ?? ""), lots: num(f.lots) ?? 0, head: num(f.head) ?? 0 })),
    states: arr("states").map((x) => ({ state: String(x.state ?? ""), lots: num(x.lots) ?? 0, head: num(x.head) ?? 0, dollars: num(x.dollars) ?? 0,
      vwap: num(x.vwap), barns: num(x.barns) ?? 0, sharePct: num(x.share_pct) })),
    barns: arr("barns").map((x) => ({ state: String(x.state ?? ""), barn: String(x.barn ?? ""), lots: num(x.lots) ?? 0, head: num(x.head) ?? 0,
      dollars: num(x.dollars) ?? 0, vwap: num(x.vwap), sharePct: num(x.share_pct) })),
    lotsTotal: num(d.lots_total) ?? 0, lotsInline: d.lots_inline === true, lots: arr("lots").map(lot),
    decisions: arr("decisions").map((x) => ({ lotId: num(x.lot_id) ?? 0, date: String(x.date ?? ""), barn: String(x.barn ?? ""),
      state: String(x.state ?? ""), cattleType: String(x.cattle_type ?? ""), avgWeight: num(x.avg_weight) ?? 0, head: num(x.head) ?? 0,
      priceCwt: num(x.price_cwt) ?? 0, dollars: num(x.dollars) ?? 0, status: String(x.status ?? ""),
      reason: x.reason == null ? null : String(x.reason), note: x.note == null ? null : String(x.note),
      movedTo: x.moved_to == null ? null : String(x.moved_to), decidedAt: String(x.decided_at ?? ""), role: String(x.role ?? "admin"),
      effect: String(x.effect ?? "") })),
    revisions: arr("revisions").map((x) => ({ revision: num(x.revision) ?? 1, status: String(x.status ?? ""),
      reason: x.reason == null ? null : String(x.reason), computedAt: String(x.computed_at ?? ""), priceCwt: num(x.price_cwt) ?? 0,
      head: num(x.head) ?? 0, lots: num(x.lots) ?? 0, pounds: num(x.pounds), coverage: x.coverage == null ? null : String(x.coverage),
      methodologyVersion: x.methodology_version == null ? null : String(x.methodology_version),
      deltaCwt: num(x.delta_cwt), deltaHead: num(x.delta_head), deltaLots: num(x.delta_lots), decisionsBetween: num(x.decisions_between) ?? 0 })),
    pendingRevision: d.pending_revision == null ? null : d.pending_revision === true,
    readings: rd ? { open: num(rd.open) ?? 0, high: num(rd.high) ?? 0, low: num(rd.low) ?? 0, last: num(rd.last) ?? 0,
      firstAt: String(rd.first_at ?? ""), lastAt: String(rd.last_at ?? ""), marks: num(rd.marks) ?? 0, stepMinutes: num(rd.step_minutes) ?? 0,
      unknownArrivalLots: num(rd.unknown_arrival_lots) ?? 0, lateLots: num(rd.late_lots) ?? 0 } : null,
    vintage: { source: String(vt.source ?? ""), firstArrival: vt.first_arrival == null ? null : String(vt.first_arrival),
      lastArrival: vt.last_arrival == null ? null : String(vt.last_arrival), unknownArrivalLots: num(vt.unknown_arrival_lots) ?? 0,
      generatedAt: String(vt.generated_at ?? "") },
  };
}

/** Every counted lot, however many — pages of 5,000 behind the sheet's CSV. */
/** Every counted lot (the CSV), or — with `barn` — one barn's lots, for a
 *  barn box on a sheet whose day carries more than the 3,000 the page lists. */
export async function fetchRbIndexSheetLots(slug: string, asOf: string | null, key: string | null,
                                            barn?: { state: string; barn: string }): Promise<SheetLot[]> {
  const out: SheetLot[] = [];
  for (let offset = 0; offset < 200_000; offset += 5000) {
    const d = await rpcKeyed<Record<string, unknown>>("rb_index_sheet_lots", {
      p_slug: slug, p_as_of: asOf, p_key: key || null, p_offset: offset, p_limit: 5000,
      p_state: barn?.state ?? null, p_barn: barn?.barn ?? null,
    });
    const rows = ((d?.rows as Record<string, unknown>[] | undefined) ?? []).map(lot);
    out.push(...rows);
    if (rows.length < 5000) break;
  }
  return out;
}


/* ── The corrections log ─────────────────────────────────────────────────────
   Owner, 2026-10-07: "does the end-user see these?" — every revision of a
   published close, before and after, in public. Read from the append-only
   record; nothing here is lot-level. */
export interface RbIndexCorrection {
  slug: string;
  code: string;
  name: string;
  asOf: string;
  revision: number;
  revisedAt: string;
  firstWrittenAt: string | null;
  before: number | null;
  after: number | null;
  change: number | null;
  beforeHead: number | null;
  afterHead: number | null;
  beforeLots: number | null;
  afterLots: number | null;
  reason: string | null;
  lotsRemoved: number;
  lotsMoved: number;
  removalReasons: string | null;
}

export type CorrectionsDays = 30 | 90 | 400;

export async function fetchRbIndexCorrections(days: CorrectionsDays = 400): Promise<RbIndexCorrection[]> {
  const rows = (await rpc<Record<string, unknown>>("rb_index_corrections", { p_days: String(days) })) ?? [];
  return rows.map((r) => ({
    slug: String(r.slug ?? ""), code: String(r.code ?? ""), name: String(r.name ?? ""), asOf: String(r.as_of ?? ""),
    revision: num(r.revision) ?? 0, revisedAt: String(r.revised_at ?? ""),
    firstWrittenAt: r.first_written_at == null ? null : String(r.first_written_at),
    before: num(r.before_cwt), after: num(r.after_cwt), change: num(r.change),
    beforeHead: num(r.before_head), afterHead: num(r.after_head), beforeLots: num(r.before_lots), afterLots: num(r.after_lots),
    reason: r.reason == null ? null : String(r.reason),
    lotsRemoved: num(r.lots_removed) ?? 0, lotsMoved: num(r.lots_moved) ?? 0,
    removalReasons: r.removal_reasons == null ? null : String(r.removal_reasons),
  }));
}
